"use client";

export type AppLanguage = "en" | "ko";

export const ACTIVE_SESSION_STORAGE_KEY = "aitoktok.activeSessionId.tab";
export const LANGUAGE_STORAGE_KEY = "aitoktok-language";
export const SIDEBAR_COLLAPSED_STORAGE_KEY = "aitoktok.sidebarCollapsed";
export const ACTIVE_KNOWLEDGE_STORAGE_KEY = "aitoktok.activeKnowledge";
export const CHAT_RESPONSE_TIMEOUT_STORAGE_KEY =
  "aitoktok.chatResponseTimeout";

// Mirror of the per-user ``chat_response_timeout`` UI preference. Cached in
// localStorage so the chat watchdog (a separate provider from Settings) can
// read it synchronously without its own fetch. Kept in sync on settings load.
export const DEFAULT_CHAT_RESPONSE_TIMEOUT_SECONDS = 180;
export const MIN_CHAT_RESPONSE_TIMEOUT_SECONDS = 30;
export const MAX_CHAT_RESPONSE_TIMEOUT_SECONDS = 1800;

export function clampChatResponseTimeout(seconds: number): number {
  if (!Number.isFinite(seconds)) return DEFAULT_CHAT_RESPONSE_TIMEOUT_SECONDS;
  return Math.min(
    MAX_CHAT_RESPONSE_TIMEOUT_SECONDS,
    Math.max(MIN_CHAT_RESPONSE_TIMEOUT_SECONDS, Math.round(seconds)),
  );
}

export function readStoredChatResponseTimeout(): number {
  if (typeof window === "undefined")
    return DEFAULT_CHAT_RESPONSE_TIMEOUT_SECONDS;
  try {
    const raw = window.localStorage.getItem(CHAT_RESPONSE_TIMEOUT_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0
      ? clampChatResponseTimeout(parsed)
      : DEFAULT_CHAT_RESPONSE_TIMEOUT_SECONDS;
  } catch {
    return DEFAULT_CHAT_RESPONSE_TIMEOUT_SECONDS;
  }
}

export function writeStoredChatResponseTimeout(seconds: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CHAT_RESPONSE_TIMEOUT_STORAGE_KEY,
      String(clampChatResponseTimeout(seconds)),
    );
  } catch {
    // localStorage may be unavailable
  }
}

export const ACTIVE_SESSION_EVENT = "aitoktok:active-session";
export const LANGUAGE_EVENT = "aitoktok:language";
export const SIDEBAR_COLLAPSED_EVENT = "aitoktok:sidebar-collapsed";
export const ACTIVE_KNOWLEDGE_EVENT = "aitoktok:active-knowledge";

/**
 * 언어 코드 정규화 SSOT.
 *
 * 지원 언어는 ko/en 2종이고 기본은 ko다. 명시적인 영어 표기만 en 으로 떨어지고
 * 나머지(빈 값, 미지원 코드, 과거 zh 값)는 전부 ko 로 수렴한다.
 * `web/i18n/init.ts` 의 동명 함수는 이 구현에 위임한다 — 두 벌로 두면
 * 기본값이 갈려 first-paint 와 저장값이 어긋난다.
 */
export function normalizeLanguage(
  value: string | null | undefined,
): AppLanguage {
  const s = (value ?? "").trim().toLowerCase();
  if (s === "en" || s === "english" || s.startsWith("en-") || s.startsWith("en_")) {
    return "en";
  }
  return "ko";
}

export function readStoredLanguage(): AppLanguage {
  // 저장값이 있으면 그 값을 존중하고, 없을 때만 기본 ko로 떨어진다.
  if (typeof window === "undefined") return "ko";
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === null ? "ko" : normalizeLanguage(stored);
  } catch {
    return "ko";
  }
}

/**
 * Safe localStorage accessors.
 *
 * Storage throws — not returns null — when the browser blocks it (Safari private
 * mode, "block all cookies", enterprise policy). An unguarded read in a globally
 * rendered component takes the whole app down, so every call site goes through here.
 */
export function safeLocalGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeLocalSet(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — the preference simply does not persist.
  }
}

/**
 * Raw stored language, or `null` when this browser has never chosen one.
 * Callers use it to tell "no preference yet" apart from "explicitly chose ko".
 */
export function readStoredLanguageRaw(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredLanguage(language: AppLanguage): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    window.dispatchEvent(
      new CustomEvent(LANGUAGE_EVENT, {
        detail: { language },
      }),
    );
  } catch {
    // localStorage may be unavailable
  }
}

export function readStoredActiveSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredActiveSessionId(sessionId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionId) {
      window.sessionStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, sessionId);
    } else {
      window.sessionStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
    }
    window.dispatchEvent(
      new CustomEvent(ACTIVE_SESSION_EVENT, {
        detail: { sessionId },
      }),
    );
  } catch {
    // sessionStorage may be unavailable
  }
}
export function readStoredActiveKnowledge(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ACTIVE_KNOWLEDGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredActiveKnowledge(name: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (name) {
      window.localStorage.setItem(ACTIVE_KNOWLEDGE_STORAGE_KEY, name);
    } else {
      window.localStorage.removeItem(ACTIVE_KNOWLEDGE_STORAGE_KEY);
    }
    window.dispatchEvent(
      new CustomEvent(ACTIVE_KNOWLEDGE_EVENT, {
        detail: { name },
      }),
    );
  } catch {
    // localStorage may be unavailable
  }
}

export const RECENT_KNOWLEDGE_STORAGE_KEY = "aitoktok.recentKnowledge";
export const RECENT_KNOWLEDGE_LIMIT = 10;

export function readStoredRecentKnowledge(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KNOWLEDGE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === "string" && !!item)
      .slice(0, RECENT_KNOWLEDGE_LIMIT);
  } catch {
    return [];
  }
}

export function writeStoredRecentKnowledge(names: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      RECENT_KNOWLEDGE_STORAGE_KEY,
      JSON.stringify(names.slice(0, RECENT_KNOWLEDGE_LIMIT)),
    );
  } catch {
    // localStorage may be unavailable
  }
}

export function readStoredSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeStoredSidebarCollapsed(collapsed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      collapsed ? "1" : "0",
    );
    window.dispatchEvent(
      new CustomEvent(SIDEBAR_COLLAPSED_EVENT, {
        detail: { collapsed },
      }),
    );
  } catch {
    // localStorage may be unavailable
  }
}
